import resemble from 'resemblejs';

// Configure ResembleJS output settings
resemble.outputSettings({
  errorType: 'movement',
  largeImageThreshold: 1400,
});

const STRUCTURAL_WEIGHT = 0.7;
const COLOR_WEIGHT = 0.3;
const SCORE_CURVE_EXPONENT = 0.8;

const runComparison = (
  userScreenshot: string,
  designScreenshot: string,
  ignoreColors: boolean
): Promise<number> => {
  return new Promise((resolve) => {
    let comparison = resemble(userScreenshot)
      .compareTo(designScreenshot)
      .scaleToSameSize()
      .ignoreAntialiasing();

    if (ignoreColors) {
      comparison = comparison.ignoreColors();
    }

    comparison.onComplete((data) => {
      if (data.error) {
        resolve(0);
      } else {
        const mismatchPercentage = parseFloat(data.misMatchPercentage);
        resolve(Math.ceil(100 - mismatchPercentage));
      }
    });
  });
};

const applyScoreCurve = (rawScore: number): number => {
  if (rawScore <= 0) return 0;
  if (rawScore >= 100) return 100;

  const boosted = 100 * Math.pow(rawScore / 100, SCORE_CURVE_EXPONENT);

  return Math.min(100, Math.ceil(boosted));
};

/**
 * Compares two images using a blended approach:
 * - 70% structural similarity (ignoring colors)
 * - 30% color accuracy
 * Then applies a gentle curve to boost mid-range scores.
 */
export const compareDesignImages = async (
  userScreenshot: string,
  designScreenshot: string
): Promise<number> => {
  const [structuralScore, colorScore] = await Promise.all([
    runComparison(userScreenshot, designScreenshot, true),
    runComparison(userScreenshot, designScreenshot, false),
  ]);

  const blendedScore = Math.ceil(
    structuralScore * STRUCTURAL_WEIGHT + colorScore * COLOR_WEIGHT
  );

  return applyScoreCurve(blendedScore);
};
